import { CSSProperties, ReactNode, RefObject, useEffect, useLayoutEffect, useRef, useState } from "react"

import { CSSTransition } from "react-transition-group"

import useHover from "$/hooks/use-hover"

import { buildCssClassName } from "@/3514/utils"
import { SECONDARY_PORTAL_ID } from "@/constants"

import Portal from "../portal/Portal"

import { calculateTooltipPosition } from "./calculate-tooltip-position"
import { TooltipDimensions, TooltipPosition, TooltipStyle } from "./types"

import "./styles.scss"

interface TooltipProps {
    children: ReactNode
    targetRef: RefObject<HTMLElement>
    position?: TooltipPosition
    offset?: number | { x: number; y: number }
    style?: CSSProperties
    className?: string
}

export const Tooltip = ({
    children,
    targetRef,
    position = "top",
    style = {},
    offset = 10,
    className = ""
}: TooltipProps) => {
    const isVisible = useHover(targetRef)
    const [tooltipDimensions, setTooltipDimensions] = useState<TooltipDimensions>({ width: 0, height: 0 })
    const [tooltipStyle, setTooltipStyle] = useState<TooltipStyle>({})
    const tooltipRef = useRef<HTMLDivElement>(null)

    const offsetX = typeof offset === "number" ? offset : (offset?.x ?? 10)
    const offsetY = typeof offset === "number" ? offset : (offset?.y ?? 10)

    useEffect(() => {
        if (tooltipRef.current && isVisible && tooltipDimensions.width === 0) {
            setTooltipDimensions({
                width: tooltipRef.current.offsetWidth,
                height: tooltipRef.current.offsetHeight
            })
        }

        if (isVisible) {
            document.documentElement.style.setProperty(
                "--tooltip-offset",
                `${typeof offset === "number" ? offset : 10}px`
            )
        }

        return () => {
            document.documentElement.style.removeProperty("--tooltip-offset")
        }
    }, [isVisible])

    useLayoutEffect(() => {
        if (targetRef.current && tooltipDimensions.width > 0 && tooltipDimensions.height > 0) {
            const bound = targetRef.current.getBoundingClientRect()

            const { adjustedLeft, adjustedTop } = calculateTooltipPosition(
                position,
                tooltipDimensions,
                { offsetX, offsetY },
                bound
            )

            const newStyle: TooltipStyle = {
                ...style,
                position: "absolute",
                zIndex: 1000,
                left: adjustedLeft,
                top: adjustedTop
            }

            if (JSON.stringify(newStyle) !== JSON.stringify(tooltipStyle)) {
                setTooltipStyle(newStyle)
            }
        }
    }, [position, style, tooltipDimensions, offset])

    return (
        <Portal portalId={SECONDARY_PORTAL_ID}>
            <CSSTransition
                in={isVisible}
                timeout={300}
                classNames={`tooltip-w-${position} tooltip-w`}
                unmountOnExit
                nodeRef={tooltipRef}
            >
                <div style={tooltipStyle} ref={tooltipRef} className={buildCssClassName("tooltip-i", className)}>
                    {children}
                </div>
            </CSSTransition>
        </Portal>
    )
}
